import { Fragment, useMemo, useState } from 'react';
import { Button, Alert } from '@mui/material';
import useApi from 'shared/hooks/api';
import api from 'utils/api';
import { PageLoader, PageError, Modal } from 'components';

import DetailsModal from 'views/admin/components/DetailsModal';
import { DOLPHIN_API } from 'shared/constants/apis';
import BasicTable from 'components/BasicTable';
import { reportTemplateTableColumns } from './tableColumns';
import { ConfirmModal } from 'shared/modals/confirmModal';
import logger from 'utils/logger';
import { NewReportModal } from './components/NewReportModal';
import { useSnackbar } from 'notistack';

const log = logger.createLogger('AdminReportTemplates');

const AdminReportTemplates = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [reportDetailsModalOpen, setReportDetailsModalOpen] = useState(false);
  const [reportIdSelected, setReportIdSelected] = useState(-1);
  const [reportDataSelected, setReportDataSelected] = useState({});
  const [reportNewModalOpen, setReportNewModalOpen] = useState(false);
  const [reportDeleteModalOpen, setReportDeleteModalOpen] = useState(false);

  const handleOpenReportDetails = (reportData) => {
    setReportIdSelected(reportData.id);
    setReportDataSelected(reportData);
    setReportDetailsModalOpen(true);
  };

  const handleOpenDeleteReport = (reportId) => {
    setReportIdSelected(reportId);
    setReportDeleteModalOpen(true);
  };

  const handleUpdateReport = async (_, updatedReport) => {
    try {
      // Parse the JSON string if it's a string
      const parsedReport =
        typeof updatedReport === 'string'
          ? JSON.parse(updatedReport)
          : updatedReport;

      await api.put(DOLPHIN_API.ADMIN_REPORT_TEMPLATES, {
        data: {
          templateId: parsedReport.id,
          organizationIds: parsedReport.organizations,
          title: parsedReport.title,
          s3FilePath: parsedReport.s3FilePath,
          content: parsedReport.content,
        },
      });
      await fetchReportTemplates();
      enqueueSnackbar('Report template updated successfully', {
        variant: 'success',
      });
    } catch (error) {
      log.error('Error updating report template:', error);
      enqueueSnackbar(
        `Error updating report template: ${error.message || error}`,
        {
          variant: 'error',
        }
      );
    }
  };

  const deleteReport = async (reportId) => {
    try {
      await api.delete(DOLPHIN_API.ADMIN_REPORT_TEMPLATES, {
        data: {
          templateId: reportId,
        },
      });
      await fetchReportTemplates();
    } catch (error) {
      console.error('Error deleting report template:', error);
      // Show the complete error message to the user
      alert(`Error deleting report template: ${error.message || error}`);
    }
    setReportDeleteModalOpen(false);
  };

  const columns = useMemo(
    () =>
      reportTemplateTableColumns(
        handleOpenReportDetails,
        handleOpenDeleteReport
      ),
    [handleOpenReportDetails, handleOpenDeleteReport]
  );

  const [{ data, error }, fetchReportTemplates] = useApi.useGet(
    DOLPHIN_API.ADMIN_REPORT_TEMPLATES,
    {}
  );
  if (!data) return <PageLoader />;
  if (error)
    return (
      <PageError
        errorCode={error.response.status}
        customErrorMessage={error.response.data.message}
        customErrorDescription={error.response.data.description}
      />
    );

  const tableData = data.reportTemplates.map((template) => {
    return {
      id: template.id,
      title: template.title,
      s3FilePath: template.s3FilePath,
      orgId: template.organizations.map((org) => org.id).join(','),
      details: {
        id: template.id,
        isLocal: template.isLocal,
        content: template.content,
        s3FilePath: template.s3FilePath,
        title: template.title,
        organizations: template.organizations.map((org) => org.id),
      },
      delete: template,
    };
  });

  return (
    <Fragment>
      <BasicTable
        columns={columns}
        data={tableData}
        handleRefreshClick={fetchReportTemplates}
        tableTitle="Report Templates"
        rowsPerPageOptions={[10, 20, 50, 100]}
        initialPageSize={50}
        customToolbar={
          <>
            <Button
              color="primary"
              variant="contained"
              onClick={() => {
                setReportNewModalOpen(true);
              }}>
              New
            </Button>
            <Alert severity="info" sx={{ mb: 1, py: 1 }}>
              Please upload the images and PDF to the S3 inside the folder:
              /templates/
              {'{report_template_id}'}
            </Alert>
          </>
        }
      />

      {/* This is irregular */}
      <Modal
        isOpen={reportNewModalOpen}
        width="700px"
        withCloseIcon={true}
        onClose={() => setReportNewModalOpen(false)}
        renderContent={(modal) => (
          <NewReportModal
            close={() => modal.close()}
            reloadTemplates={fetchReportTemplates}
          />
        )}
      />

      <Modal
        isOpen={reportDetailsModalOpen}
        width="60%"
        maxHeight="800px"
        withCloseIcon={true}
        onClose={() => setReportDetailsModalOpen(false)}
        renderContent={() => (
          <DetailsModal
            dataType="Report Template"
            dataId={reportIdSelected}
            data={reportDataSelected}
            updateData={handleUpdateReport}
          />
        )}
      />

      <ConfirmModal
        isOpened={reportDeleteModalOpen}
        onConfirm={() => deleteReport(reportIdSelected)}
        onCancel={() => setReportDeleteModalOpen(false)}
        title={`Do you want to DELETE Report Template with ID: ${reportIdSelected}`}
        confirmButtonText="Delete"
        cancelButtonText="Cancel"
      />
    </Fragment>
  );
};

export default AdminReportTemplates;
